import React from 'react';
import { SidePanelHandle } from 'shared/components/side_panel';
import { Row, TalentMatchingApplication } from 'talent_matching/types';
import useCandidateSidePanel from 'talent_matching/hooks/useCandidateSidePanel';
import { useRediscoveryTabContext } from 'talent_matching/contexts/rediscovery_tab_context';
import { useRediscoveryRowActionsModal } from 'talent_matching/contexts/rediscovery_row_actions_modal_context';
// eslint-disable-next-line max-len
import RediscoveryApplicationHistory from 'talent_matching/components/side_panel/rediscovery/rediscovery_application_history';
import { useUserContext } from 'talent_matching/contexts/user_context';
import LinkedinActivitySection from 'talent_matching/components/side_panel/linkedin_activity_section';
import BaseSidePanel, { SidePanelNavigationProps } from './base_side_panel';
import RediscoverySidePanelHeader from './rediscovery_side_panel_header';

type Props = SidePanelNavigationProps & {
  candidatePrefetchBatch: Row[];
};

const TalentRediscoverySidePanel = React.forwardRef<SidePanelHandle, Props>(
  (
    {
      onClose,
      applicationId,
      onToggle,
      isVisible,
      rowCount,
      currentIndex,
      previousCandidate,
      nextCandidate,
      selectCandidate,
      candidatePrefetchBatch,
    },
    ref
  ) => {
    const { linkedinRscEnabled } = useUserContext();
    const { hasOpenModal } = useRediscoveryRowActionsModal();
    const { activeSidePanelTab, setActiveSidePanelTab } =
      useRediscoveryTabContext();

    const { handlePanelRef, application, isLoading } = useCandidateSidePanel({
      applicationId,
      ref,
      prefetchCandidatesBatch: candidatePrefetchBatch,
      isRediscovery: true,
    });

    return (
      <BaseSidePanel
        handlePanelRef={handlePanelRef}
        application={application as TalentMatchingApplication}
        isLoading={isLoading}
        onClose={onClose}
        onToggle={onToggle}
        isVisible={isVisible}
        rowCount={rowCount}
        currentIndex={currentIndex}
        previousCandidate={previousCandidate}
        nextCandidate={nextCandidate}
        selectCandidate={selectCandidate}
        applicationId={applicationId}
        activeSidePanelTab={activeSidePanelTab}
        setActiveSidePanelTab={setActiveSidePanelTab}
        externalDisableClickOutside={hasOpenModal}
        externalModalsOpen={hasOpenModal}
        renderHeader={({ application: app }) => (
          <RediscoverySidePanelHeader application={app} />
        )}
      >
        {({
          application: app,
          isLoading: loading,
          setLinkedinPreview,
          extendedResumePreview,
          modalDocument,
          setExtendedResumePreview,
          setModalDocument,
          allowExtendedPreview,
        }) => (
          <>
            {linkedinRscEnabled && (
              <LinkedinActivitySection
                linkedinAddress={app.linkedinAddress}
                onViewLinkedin={
                  app.linkedinIframePath
                    ? () => {
                        setLinkedinPreview(
                          app.linkedinIframePath as string | null
                        );
                      }
                    : undefined
                }
              />
            )}
            {!loading && (
              <RediscoveryApplicationHistory
                historyUrl={app.applicationHistoryPath}
                activeDocument={extendedResumePreview ?? modalDocument ?? null}
                onOpenDocument={
                  allowExtendedPreview
                    ? (doc) => {
                        setModalDocument(null);
                        setLinkedinPreview(null);
                        setExtendedResumePreview(doc);
                      }
                    : (doc) => {
                        setExtendedResumePreview(null);
                        setModalDocument(doc);
                      }
                }
              />
            )}
          </>
        )}
      </BaseSidePanel>
    );
  }
);

TalentRediscoverySidePanel.displayName = 'TalentRediscoverySidePanel';

export default TalentRediscoverySidePanel;
